/**
 * Pinochle meld scoring (single-deck values on the double-deck game).
 *
 *  - Run (A 10 K Q J of trump) 15, double run 150.
 *  - Royal marriage (K Q of trump) 4, common marriage (K Q of another suit) 2. Cards in a run are not counted again
 *    as a marriage.
 *  - Dix (9 of trump) 1 each.
 *  - Pinochle (Q♠ + J♦) 4, double pinochle 30.
 *  - Aces around (one ace of each suit) 10, double 100; kings around 8 / 80; queens around 6 / 60; jacks around 4 / 40.
 */
import { parseCard, SUITS, type CardId, type Suit } from '../../core/cards.js';

export interface PinochleMeldItem {
  type: 'run' | 'doubleRun' | 'royal' | 'marriage' | 'dix' | 'pinochle' | 'doublePinochle' | 'aces' | 'kings' | 'queens' | 'jacks' | 'doubleAces' | 'doubleKings' | 'doubleQueens' | 'doubleJacks';
  points: number;
  cards: CardId[];
}

export interface PinochleMeld {
  points: number;
  items: PinochleMeldItem[];
}

const RUN_RANKS = [14, 10, 13, 12, 11] as const;
const AROUND: { rank: number; single: number; double: number; type: 'aces' | 'kings' | 'queens' | 'jacks'; doubleType: 'doubleAces' | 'doubleKings' | 'doubleQueens' | 'doubleJacks' }[] = [
  { rank: 14, single: 10, double: 100, type: 'aces', doubleType: 'doubleAces' },
  { rank: 13, single: 8, double: 80, type: 'kings', doubleType: 'doubleKings' },
  { rank: 12, single: 6, double: 60, type: 'queens', doubleType: 'doubleQueens' },
  { rank: 11, single: 4, double: 40, type: 'jacks', doubleType: 'doubleJacks' },
];

/** Cards of a suit and rank in a hand (both copies). */
const of = (hand: readonly CardId[], suit: Suit, rank: number): CardId[] => hand.filter((c) => {
  const p = parseCard(c);
  return p.suit === suit && p.rank === rank;
});

export function pinochleMeld(hand: readonly CardId[], trump: Suit): PinochleMeld {
  const items: PinochleMeldItem[] = [];
  const add = (type: PinochleMeldItem['type'], points: number, cards: CardId[]) => items.push({ type, points, cards });

  // Runs in trump.
  const runCards = RUN_RANKS.map((r) => of(hand, trump, r));
  const runs = Math.min(...runCards.map((c) => c.length));
  if (runs === 2) add('doubleRun', 150, runCards.flat());
  else if (runs === 1) add('run', 15, runCards.map((c) => c[0]!));

  // Marriages: kings and queens of each suit not already used in a run.
  for (const suit of SUITS) {
    const kings = of(hand, suit, 13);
    const queens = of(hand, suit, 12);
    const used = suit === trump ? runs : 0;
    const pairs = Math.min(kings.length, queens.length) - used;
    for (let i = 0; i < pairs; i++) {
      const k = kings[used + i]!;
      const q = queens[used + i]!;
      if (suit === trump) add('royal', 4, [k, q]);
      else add('marriage', 2, [k, q]);
    }
  }

  // Dix: nines of trump.
  for (const nine of of(hand, trump, 9)) add('dix', 1, [nine]);

  // Pinochle: queen of spades and jack of diamonds.
  const qs = of(hand, 'S', 12);
  const jd = of(hand, 'D', 11);
  const pin = Math.min(qs.length, jd.length);
  if (pin === 2) add('doublePinochle', 30, [...qs, ...jd]);
  else if (pin === 1) add('pinochle', 4, [qs[0]!, jd[0]!]);

  // Aces / kings / queens / jacks around.
  for (const a of AROUND) {
    const perSuit = SUITS.map((s) => of(hand, s, a.rank));
    const n = Math.min(...perSuit.map((c) => c.length));
    if (n === 2) add(a.doubleType, a.double, perSuit.flat());
    else if (n === 1) add(a.type, a.single, perSuit.map((c) => c[0]!));
  }

  return { points: items.reduce((n, i) => n + i.points, 0), items };
}
